import { z } from 'zod';
import { JobState, Locale, MangaPhase, RenderTier } from './enums';

export const BubbleBox = z.object({
  /**
   * Normalised by the art's width (x, w) and height (y, h), measured from the top of the panel block:
   * the lettering strip first, then the art. Computed by the server layout engine.
   */
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  tail: z.enum(['left', 'right', 'none']),
  style: z.enum(['speech', 'thought', 'caption']),
});
export type BubbleBox = z.infer<typeof BubbleBox>;
/** Lettering type size as a fraction of panel width, shared so the server's bubble layout matches what the app draws. */
export const BUBBLE_FONT_FRAC = 0.036;
/** Bubble inner padding as fractions of panel width (horizontal, vertical). */
export const BUBBLE_PAD_FRAC = { x: 0.034, y: 0.022 } as const;

export const PanelText = z.object({
  speakerId: z.string().nullable(),
  speakerName: z.string().nullable(),
  text: z.string(),
  /** True only for words the player actually typed/chose (plan §12 台詞). */
  verbatim: z.boolean(),
  /** Where the app letters this line; null when the words are drawn into the art itself (lettering 'in_art'). */
  box: BubbleBox.nullable(),
});
export type PanelText = z.infer<typeof PanelText>;

export const PanelView = z.object({
  panelId: z.string(),
  index: z.number().int(),
  imageUrl: z.string(),
  thumbUrl: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  texts: z.array(PanelText),
  /**
   * 'overlay': the app letters `texts` natively over/above the art. 'in_art': a full AI-drawn page whose
   * balloons the server lettered into the image; `texts` is then its transcript (screen readers, script view).
   */
  lettering: z.enum(['overlay', 'in_art']).default('overlay'),
  /** Height of the lettering strip above the art, as a fraction of the art's height. 0 = older renders lettered over the art. */
  letterHeight: z.number().min(0).max(1).default(0),
  altText: z.string(),
  status: z.enum(['ready', 'fallback', 'failed']),
  /** Illustrated renders: the page (1-based) this panel is on, matching `illustration.composedPages`. null on basic renders. */
  page: z.number().int().nullable().default(null),
});
export type PanelView = z.infer<typeof PanelView>;

export const RenderView = z.object({
  renderId: z.string(),
  renderVersion: z.number().int(),
  locale: Locale,
  tier: RenderTier,
  styleVersion: z.string(),
  panels: z.array(PanelView),
  pageImageUrl: z.string().nullable(),
  /**
   * Illustrated renders: which pages the image model drew and which we composed from our art instead
   * (a page that couldn't be drawn, or past a free preview). Credits are charged only when every page is drawn.
   * null on basic renders and on illustrated renders made before this was recorded.
   */
  illustration: z
    .object({
      pagesTotal: z.number().int(),
      pagesDrawn: z.number().int(),
      /** 1-based page numbers composed instead of drawn; their panels have lettering 'overlay'. */
      composedPages: z.array(z.number().int()),
      /** Set when this is the free first chapter drawn as a preview of this many pages; the rest can be drawn later. */
      previewPages: z.number().int().nullable(),
      creditsCharged: z.number().int(),
    })
    .nullable()
    .default(null),
});
export type RenderView = z.infer<typeof RenderView>;

/** Where the player is with a chapter: never opened (or drawn again since), part-way, or read to the end. */
export const ReadState = z.enum(['new', 'reading', 'read']);
export type ReadState = z.infer<typeof ReadState>;

/** 下回預告: where this save's story goes after a chapter. */
export const SerialTeaser = z.object({
  templateKey: z.string(),
  title: z.string(),
  synopsis: z.string(),
  available: z.boolean(),
  lockedReason: z.string().nullable(),
});
export type SerialTeaser = z.infer<typeof SerialTeaser>;

/** 我的連載: the save's serial in reading order, and the chapter it continues into. */
export const SerialBookView = z.object({
  entries: z.array(
    z.object({
      serialNo: z.number().int(),
      title: z.string(),
      /** null when that chapter was lived but no manga was made for it. */
      chapterId: z.string().nullable(),
      coverUrl: z.string().nullable(),
      phase: MangaPhase.nullable(),
      readState: ReadState.nullable().default(null),
      /** How far into the chapter's current edition (0–1). */
      readProgress: z.number().min(0).max(1).default(0),
      gameDay: z.number().int().nullable().default(null),
    }),
  ),
  next: z.object({ serialNo: z.number().int(), teaser: SerialTeaser }).nullable(),
});
export type SerialBookView = z.infer<typeof SerialBookView>;

/**
 * A chapter read inside a character's volume (沈一諾篇): its number there, the chapters before and after
 * it in that volume, the recap of the volume's previous chapter and what comes next with them.
 */
export const VolumePlace = z.object({
  volumeId: z.string(),
  npcId: z.string(),
  /** The volume's title, e.g. 「沈一諾 篇」. */
  title: z.string(),
  /** 'lead': one of the volume's chapters; 'cameo': led by someone else, the neighbour only appears. */
  role: z.enum(['lead', 'cameo']),
  /** 1-based place among the volume's chapters; null for a cameo. */
  no: z.number().int().nullable(),
  total: z.number().int(),
  prevChapterId: z.string().nullable(),
  nextChapterId: z.string().nullable(),
  /** 上回提要 from the volume's previous chapter. */
  recap: z.string().nullable(),
  /** 下回預告 with this neighbour, when nothing in the volume comes after this chapter yet. */
  teaser: SerialTeaser.nullable(),
});
export type VolumePlace = z.infer<typeof VolumePlace>;

/** GET /manga/chapters/:id — `volume` (npc:<id>) when read from a character's volume. */
export const ChapterQuery = z.object({ volume: z.string().max(40).optional() });

export const ChapterView = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string(),
  episodeId: z.string(),
  isCanonical: z.boolean(),
  isShared: z.boolean(),
  phase: MangaPhase,
  coverUrl: z.string().nullable(),
  /** The neighbour the chapter is about (first of its cast), who stands in its hero; null when none. */
  leadNpcId: z.string().nullable().default(null),
  render: RenderView.nullable(),
  activeJobId: z.string().nullable(),
  /** The latest full-chapter try that couldn't draw every page: not charged (its ticket came back). */
  illustrationUnfinished: z.object({ pagesDrawn: z.number().int(), pagesTotal: z.number().int() }).nullable().default(null),
  favourite: z.boolean(),
  /** The panel to pick up from: set while this edition is read part-way, null to start at the cover. */
  resumePanel: z.number().int().nullable().default(null),
  createdAt: z.string(),
  gameDay: z.number().int(),
  /** 第 N 話 in this save's serial; null for side stories (encounters, what-ifs, shared chapters). */
  serialNo: z.number().int().nullable(),
  /** 上回提要 */
  recap: z.string().nullable(),
  /** 下回預告 */
  teaser: SerialTeaser.nullable(),
  prevChapterId: z.string().nullable(),
  nextChapterId: z.string().nullable(),
  /** Set when the chapter was opened from a character's volume (?volume=npc:<id>). */
  volume: VolumePlace.nullable().default(null),
  /** Someone else in this shared chapter wants to share it publicly and is waiting for your answer. */
  shareRequest: z.object({ shareId: z.string(), requestedBy: z.string() }).nullable(),
});
export type ChapterView = z.infer<typeof ChapterView>;

export const LibraryEntryView = z.object({
  chapterId: z.string(),
  title: z.string(),
  coverUrl: z.string().nullable(),
  phase: MangaPhase,
  tier: RenderTier.nullable(),
  isShared: z.boolean(),
  isCanonical: z.boolean(),
  favourite: z.boolean(),
  castNames: z.array(z.string()),
  locationId: z.string(),
  gameDay: z.number().int(),
  createdAt: z.string(),
  serialNo: z.number().int().nullable(),
  /** What-if replays of the same chapter are listed once; this is how many there are (1 otherwise). */
  versions: z.number().int().min(1).default(1),
  readState: ReadState.default('new'),
  /** How far into the current edition (0–1); 1 once read to the end. */
  readProgress: z.number().min(0).max(1).default(0),
});
export type LibraryEntryView = z.infer<typeof LibraryEntryView>;

export const LibraryQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  filter: z.enum(['all', 'favourites', 'shared', 'what_if']).default('all'),
  npcId: z.string().max(20).optional(),
});

export const MangaJobRequest = z.object({
  episodeId: z.string().max(64),
  tier: RenderTier,
  locale: Locale,
});
export type MangaJobRequest = z.infer<typeof MangaJobRequest>;

export const JobView = z.object({
  id: z.string(),
  state: JobState,
  phase: MangaPhase,
  chapterId: z.string(),
  error: z.string().nullable(),
  updatedAt: z.string(),
});
export type JobView = z.infer<typeof JobView>;

export const RepairRequest = z.object({
  panelId: z.string().max(40).optional(),
  issue: z.enum(['wrong_character', 'wrong_outfit', 'story_mismatch', 'text_overflow', 'art_glitch', 'other']),
  note: z.string().max(500).optional(),
});

/** Reading progress, counted in panels so it means the same on every screen size. */
export const ReadProgress = z.object({
  renderId: z.string().max(40),
  /** The first panel on the page in view. */
  panel: z.number().int().min(0).max(300),
  of: z.number().int().min(1).max(300),
  /** The reader reached the end of the chapter. */
  done: z.boolean(),
});
export type ReadProgress = z.infer<typeof ReadProgress>;

export const LibraryPatch = z.object({ favourite: z.boolean().optional(), note: z.string().max(500).optional(), progress: ReadProgress.optional() });

/** GET /manga/continue: the chapter to read next (part-way first, else the newest unread) and how many are unread. */
export const ContinueReadingView = z.object({ entry: LibraryEntryView.nullable(), unread: z.number().int() });
export type ContinueReadingView = z.infer<typeof ContinueReadingView>;

/** Where a shared link was posted, from `?ref=` on it (e.g. threads-03). */
export const ShareRef = z.string().regex(/^[a-z0-9_-]{1,32}$/);

/** How a live share is doing: people who opened it, read to 完, and tapped the way into the game. */
export const ShareStats = z.object({
  views: z.number().int(),
  finished: z.number().int(),
  cta: z.number().int(),
  /** Views by `?ref=`, most first. */
  refs: z.array(z.object({ ref: z.string(), views: z.number().int() })),
});
export type ShareStats = z.infer<typeof ShareStats>;

export const ShareView = z.object({
  shareId: z.string(),
  status: z.enum(['pending_consent', 'active', 'revoked']),
  url: z.string().nullable(),
  pendingUserNames: z.array(z.string()),
  stats: ShareStats.nullable().default(null),
});
export type ShareView = z.infer<typeof ShareView>;

/** POST /manga/shares/:token/events — the website's reader reporting a view, a read to 完 or a tap on its way into the game. */
export const ShareEvent = z.object({ event: z.enum(['view', 'end', 'cta']), ref: ShareRef.optional() });
export type ShareEvent = z.infer<typeof ShareEvent>;

/**
 * GET /manga/shares/:token — a chapter shared publicly, as the web reader shows it: its own public
 * copies of the panels (deleted when the share is revoked), lettered by the reader like in the app.
 */
export const PublicShareView = z.object({
  title: z.string(),
  subtitle: z.string(),
  gameDay: z.number().int(),
  serialNo: z.number().int().nullable(),
  tier: RenderTier,
  /** The neighbour the chapter is about, who stands on its cover; null when none. */
  leadNpcId: z.string().nullable(),
  coverUrl: z.string().nullable(),
  panels: z.array(PanelView),
});
export type PublicShareView = z.infer<typeof PublicShareView>;
export const ConsentDecision = z.object({ decision: z.enum(['grant', 'deny', 'revoke']) });

export const DownloadGrant = z.object({ url: z.string(), expiresInSec: z.number().int() });
export type DownloadGrant = z.infer<typeof DownloadGrant>;
export type LibraryQuery = z.infer<typeof LibraryQuery>;
export type RepairRequest = z.infer<typeof RepairRequest>;
export type LibraryPatch = z.infer<typeof LibraryPatch>;
export type ConsentDecision = z.infer<typeof ConsentDecision>;
